import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Prompt, useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { isPermitted, researcherLevelPermissions, researcherPermissionsIncludingAndAfter, useFeathers } from '../../app/util';
import EditIcon from '@mui/icons-material/Edit';
import EditNoteIcon from '@mui/icons-material/Edit';
import PreviewIcon from '@mui/icons-material/Visibility';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import TranslateIcon from '@mui/icons-material/Translate';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';

import { ScrapedContentChange } from '../../app/feathers/candidates/CandidateDraft';
import {
  CampaignFinance,
  CandidateDataFieldEditor,
  CandidateSummary,
  CirclePhoto,
  CreateProfileFeedback,
  DataDetailToolbar,
  DraftPreview,
  EditCandidateProfile,
  LinkCandidateProfileEditor,
  LoadingSpinner,
  mapDraftToServerInputs,
  Modal,
  newDraftAfterError,
  PartyTag,
  ReferenceEditor,
  ResearchCandidate,
  SidePane,
  StyledMarkdown,
  Tab,
  TabControl,
  TimeTracker,
  TranslationPairDownload,
  TranslationPairEditor
} from '../../components';
import { StanceCard, BioTile } from '../../components/lower-order';

import Menu from '@mui/material/Menu';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';

import DeleteIcon from '@mui/icons-material/Delete';
import CancelIcon from '@mui/icons-material/Cancel';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import LinkList from '../../components/LinkList/LinkList';
import { useCandidateTimeTracker } from '../../app/hooks/useCandidateTimeTracker';
import { AutoAwesome, HistoryOutlined } from '@mui/icons-material';
import { CircularProgress, CircularProgressProps } from '@mui/material';
import { Application } from '@feathersjs/feathers';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import { RootState } from '../../app/redux/root-state';
import { Candidate } from '../../app/feathers/candidates/Candidate';
import { CandidateDraft } from '../../app/feathers/candidates/CandidateDraft';
import { Race } from '../../app/feathers/races/Race';
import { HistoryLocationState } from '../../app/history-location-state';
import { PriorityLevel } from '../../app/feathers/shared/PriorityLevel';
import { Issue } from '../../app/feathers/candidates/Issue';
import { APIError } from '../../app/feathers/APIError';
import { CandidateWorkflowStatus } from '../../app/feathers/candidates/CandidateWorkflowStatus';
import { SCRAPED_CONTENT_METADATA_FIELDS, ScrapedContent } from '../../app/feathers/scraped-content/scraped-content';
import CreateCandidate from '../CreateCandidate';
import { CandidateReferenceCategoryType, References } from '../../app/feathers/candidates/References';
import { RetranslateTrigger } from '../../components/RetranslateTrigger';
import { Bio } from '../../app/feathers/candidates/BaseCandidate';
import { calculateProfilingDates, PROFILING_REASON_CODES } from '../../util/calculate-profiling-dates';
import { ConvertToRunningMateModal } from '../../components/ConvertToRunningMateModal';

const DownloadTranslateIcon = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  
  .download-icon {
    position: absolute;
    bottom: -4px;
    right: -4px;
    font-size: 14px;
    background: white;
    border-radius: 50%;
    padding: 0px;
  }
`;

const RetranslateIcon = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  
  .refresh-icon {
    position: absolute;
    bottom: -4px;
    right: -4px;
    font-size: 14px;
    background: white;
    border-radius: 50%;
    padding: 0px;
  }
`;

const EditTranslateIcon = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  
  .edit-icon {
    position: absolute;
    bottom: -4px;
    right: -4px;
    font-size: 14px;
    background: white;
    border-radius: 50%;
    padding: 0px;
  }
`;

const PulsingProgressContainer = styled.div`
  position: relative;
  display: inline-flex;
  
  @keyframes pulse {
    0% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
    100% {
      opacity: 1;
    }
  }
  
  animation: pulse 2s ease-in-out infinite;
  
  .background-progress {
    position: absolute;
    top: 0;
    left: 0;
  }
`;

// Component to display all stances using StanceCard
const StancesDisplay = ({ candidate }: { candidate: Partial<CandidateDraft> | null | undefined }) => {
  if (!candidate) return null;

  const { issues = [], bios = [] } = candidate;
  
  // Format bios for display
  const biosCombined = (bios || []).map(({ type, text, sources }) => ({
    bio: text?.length > 0 ? text : 'No information for this section yet.',
    sources,
    title: type.slice(0, 1).toUpperCase() + type.slice(1) + ' background',
    style: undefined
  }));
  
  // Separate issues with and without stances
  const issuesWithStances = issues.filter((issue) => issue.stances && issue.stances.length > 0);
  const issuesWithoutStances = issues.filter((issue) => !issue.stances || issue.stances.length === 0);

  const hasContent = biosCombined.length > 0 || issues.length > 0;

  if (!hasContent) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '36px',
        }}
      >
        <Typography
          variant="body1"
          style={{
            fontWeight: 'bold',
            letterSpacing: '2px',
            opacity: 0.5,
          }}
        >
          NO INFORMATION FOR THIS CANDIDATE YET
        </Typography>
      </div>
    );
  }

  return (
    <StancesWrapper>
      {biosCombined.length > 0 && (
        <BiosSection>
          {biosCombined.map((b, i) => (
            <React.Fragment key={i}>
              {i !== 0 && <Divider />}
              <BioTile {...b} />
            </React.Fragment>
          ))}
        </BiosSection>
      )}
      {biosCombined.length > 0 && issues.length > 0 && (
        <Divider style={{ marginTop: '16px', marginBottom: '16px' }} />
      )}
      {issuesWithStances.map((issue, issueIndex) => (
        <IssueSection key={issue.key || `issue-${issueIndex}`}>
          <Typography variant='body1' color='textPrimary' style={{ fontWeight: 'bold', marginBottom: '4px' }}>{issue.title}</Typography>
          {issue.missingData && (
            <Typography variant='body1' color='textPrimary' style={{ marginTop: '8px' }}>
              {issue.text}
            </Typography>
          )}
          {!issue.missingData && issue.stances
            ?.filter(stance => stance.usefulness !== 0)
            .map((stance, stanceIndex) => (
              <StanceCard
                key={stance._id || `stance-${stanceIndex}`}
                stance={stance}
                candidateId={candidate._id}
                showActions={true}
                showIssueLikes={false}
                awaitingReview={!stance.complete}
                style={{ marginBottom: '20px' }}
              />
            ))}
        </IssueSection>
      ))}
      {issuesWithoutStances.map((issue, issueIndex) => (
        <IssueSection key={issue.key || `issue-no-content-${issueIndex}`}>
          <Typography variant='body1' color='textPrimary' style={{ fontWeight: 'bold', marginBottom: '4px' }}>{issue.title}</Typography>
          <Typography variant='body1' color='textPrimary' style={{ marginTop: '8px' }}>
            {issue.text}
          </Typography>
        </IssueSection>
      ))}
    </StancesWrapper>
  );
};

const StancesWrapper = styled.div`
  padding: 4px 0 0;
  width: 100%;
`;

const BiosSection = styled.div`
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: #EEEEEE;
`;

const IssueSection = styled.div`
  margin-bottom: 32px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const CandidateDetail = () => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const elections = useSelector((state: RootState) => state.elections.byKey);
  const user = useSelector((state: RootState) => state.user);
  const history = useHistory<HistoryLocationState>();
  const racesSearch = history?.location?.state?.racesSearch;
  const onBack = history?.location?.state?.onBack;

  // Declare state variables
  // For keeping track of the candidate and underlying race object
  const { id, raceid: raceId } = useParams<{ id: string; raceid: string }>();
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [race, setRace] = useState<Race | null>(null);
  const coveragePlan = candidate?.coveragePlan;
  const stagingDraft: Partial<CandidateDraft> = candidate?.stagingDraft ?? {};
  const [election, setElection] = useState<string | undefined>(undefined);
  const {
    name,
    issues = [] as Issue[],
    bios = [] as Bio[],
  } = (stagingDraft || candidate);
  const synced = Boolean(stagingDraft) ? stagingDraft.synced : true;
  const someContent =
    issues?.length || bios?.length;
  const officeName = race?.officeName;

  // For managing form inputs
  const [editing, setEditing] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [reviewMode, setReviewMode] = useState(false);
  const [candidateDraft, setCandidateDraft] = useState<CandidateDraft | undefined>();
  const [scrapedContent, setScrapedContent] = useState<ScrapedContent [] | undefined>();
  const [trackedTimesByArea, setTrackedTimesByArea] = useState<Record<string, number>>({});

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<APIError | null>(null);
  const [publishing, setPublishing] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [convertToRunningMateModalOpen, setConvertToRunningMateModalOpen] = useState(false);
  const [showCreateRunningMate, setShowCreateRunningMate] = useState(false);
  const [runningMateRefreshToken, setRunningMateRefreshToken] = useState(0);
  const [profiling, setProfiling] = useState(false);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [activeJob, setActiveJob] = useState<any>(null);
  const [showCancelledModal, setShowCancelledModal] = useState(false);

  const [activeTab, setActiveTab] = useState(0);
  const [moreButtonClicked, setMoreButtonClicked] = useState<HTMLButtonElement | null>(null);
  const [reviewNotesOnGoing, setReviewNotesOnGoing] = useState(null);
  const [showGiveFeedbackModal, setShowGiveFeedbackModal] = useState(false);

  // const [activeLanguage, setActiveLanguage] = useState('en');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showReturnToDraftModal, setShowReturnToDraftModal] = useState(false);

  // Determine whether to present the publish option, which allows the user to
  // sync changes from the stagingDraft to the candidate object
  const editingEnabled = candidate?.stagingDraft?.issues?.length ? candidate?.stagingDraft?.issues?.some(i => i.stances) : true;
  const electionKey = candidate?.election;
  const electionOver = electionKey && elections[electionKey] && moment().isAfter(moment.utc(elections[electionKey].date))
  const electionCertified = electionKey && elections[electionKey] && elections[electionKey].status === 'results-certified';
  
 const editPermission = true // activeLanguage === 'en'
    && isPermitted(user, electionCertified ? ['super-admin', 'editor', 'admin'] : ['super-admin', 'editor', 'admin', 'researcher', 'reviewer', 'publisher'])
    && (
      candidate?.priorityLevel 
      ? (researcherLevelPermissions as Record<number, PriorityLevel[]>)[user?.researcherLevel ?? 0]?.includes(candidate?.priorityLevel)
      : true
    );
  const reviewerPermission = isPermitted(user, ['reviewer']) && !electionCertified;
  const editorPermission = isPermitted(user, electionCertified ? ['super-admin'] : ['super-admin', 'editor']);
  const publishPermission = isPermitted(user, electionCertified
    ? ['super-admin', 'editor', 'admin']
    :
    (coveragePlan === 'basic-coverage' || (researcherLevelPermissions as Record<number, PriorityLevel[]>)[user?.publisherLevel ?? 0]?.includes((candidate?.priorityLevel ?? 'low') as PriorityLevel)
      ? ['researcher', 'reviewer', 'publisher', 'editor', 'super-admin']
      : ['super-admin', 'editor', 'publisher']
    )
  );
  const createdOver3DaysAgo = moment(candidate?.createdAt).isBefore(moment().subtract(3, 'days'));
  const deleteBlockedByRunningMate = Boolean(
    candidate?.runningMate?.candidate
    && candidate.runningMate.isPrimary === true
  );
  const deletePermission = electionCertified
    ? false
    : isPermitted(
      user,
      researcherPermissionsIncludingAndAfter(createdOver3DaysAgo ? 'publisher' : 'researcher')
    );
  const translationPermission = isPermitted(user, [
    'super-admin',
    'translations',
  ]);
  const publishable = publishPermission && !synced;
  const useLinkCandidateEditor = coveragePlan === 'basic-coverage';

  // user tier is the highest level of permissions that the user has.
  const userTier = publishPermission
    ? 'publisher'
    : reviewerPermission
    ? 'reviewer'
    : 'researcher';
  // the user's role for this page
  const userRoleForPage = useMemo(() => {
    const assignedReviewer = typeof candidate?.workflow?.assignments?.reviewer === 'string'
        ? candidate?.workflow?.assignments?.reviewer
        : candidate?.workflow?.assignments?.reviewer?._id;
    const assignedResearcher = typeof candidate?.workflow?.assignments?.researcher === 'string'
      ? candidate?.workflow?.assignments?.researcher
      : candidate?.workflow?.assignments?.researcher?._id;
    if (editorPermission) {
      return 'editor';
    }
    if (publishPermission) {
      return 'publisher';
    }
    if (reviewerPermission && user._id === assignedReviewer) {
      return 'reviewer';
    }
    if (editPermission && user._id === assignedResearcher) {
      return 'researcher';
    }
  }, [user, candidate]);

  // setup for tracking edit times
  const { startEditTracker, endEditTracker } =
    useCandidateTimeTracker(candidate?._id, userRoleForPage);

  // if workflow version does not match staging draft version.
  const [showReviewButton, setShowReviewButton] = useState(false);
  const [showSubmitForReviewButton, setShowSubmitForReviewButton] = useState(false);
  const [showProfilerConfirmModal, setShowProfilerConfirmModal] = useState(false);

  const revertToPreviousVersion = () => {
    setCandidateDraft(candidate?.stagingDraft ? {
      ...candidate.stagingDraft,
      removedAutoContent: candidate.removedAutoContent,
    } : undefined);
    setRestoring(true);
  };

  const runProfiler = async (confirmed: boolean = false) => {
    if(profiling || !candidate?._id) {
      return;
    }

    // Check if profiling should be allowed
    const profilingDecision = calculateProfilingDates(candidate);

    // If shouldProfile is false and not confirmed, show confirmation modal
    // Exception: don't show modal if profiler ran recently (within 15 minutes)
    if (!profilingDecision.shouldProfile && !confirmed && profilingDecision.reasonCode !== PROFILING_REASON_CODES.PROFILER_RAN_RECENTLY) {
      setShowProfilerConfirmModal(true);
      return;
    }

    setShowProfilerConfirmModal(false);
    setProfiling(true);

    try {
      const [ result, scrapedContent ] = await Promise.all([
        feathers.getService('profiler').create({
          candidate: candidate._id,
          manuallyCalled: true
        }),
        setTimeout(() => {
          loadScrapedContent();
        }, 2000)
      ])

      
      // Set activeJobId if jobId is returned
      if (result?.jobId) {
        setActiveJobId(result.jobId);
        setProfiling(true);
      } else {
        // If no jobId, profiling is done
        setProfiling(false);
      }

    } catch (err) {
      console.log(err)
      setProfiling(false);
    }
  }

  const cancelProfiler = async () => {
    if (!profiling || !activeJobId || !candidate?._id) {
      return;
    }

    try {
      await feathers.getService('candidates').patch(candidate._id, {
        $updateJob: {
          _id: activeJobId,
          status: 'CANCELLED',
        },
      });
      setMoreButtonClicked(null);
      // The polling effect will detect the cancelled status and update the UI
    } catch (err) {
      console.error('Error cancelling profiler:', err);
      setError(err as APIError);
    }
  }

  const determineReviewButton = () => {
    // set review button if the candidate edits are ready for the user to review
    if (userTier === 'publisher') {
      if (
        ['researcher-submitted', 'reviewer-submitted'].includes(
          candidate?.workflow?.status as string
        ) &&
        !candidate?.workflow?.rejectionStatus
      ) {
        setShowReviewButton(true);
      } else {
        setShowReviewButton(false);
      }
    } else if (userTier === 'reviewer') {
      if (
        candidate?.workflow?.status === 'researcher-submitted' &&
        !candidate?.workflow?.rejectionStatus
      ) {
        setShowReviewButton(true);
      } else {
        setShowReviewButton(false);
      }
    } else {
      setShowReviewButton(false);
    }
  };

  // determines when to display the submit for review button
  const determineSubmitForReviewButton = () => {
    // show submit for review button if user has made edits that require a review
    // researchers can submit for review drafts with changes or when they make changes after getting a rejection
    if (!synced && userTier === 'researcher') {
      if (candidate?.workflow.status === 'draft') {
        setShowSubmitForReviewButton(true);
      } else if (
        candidate?.workflow.status === 'researcher-submitted' &&
        candidate?.workflow.rejectionStatus
      ) {
        // if there is an edit to the draft after rejection-- draft user will be this researcher
        const savedStagingDraft = user._id === candidate.stagingDraft?.user;
        setShowSubmitForReviewButton(savedStagingDraft);
      } else {
        setShowSubmitForReviewButton(false);
      }
      // reviewers can submit for review something in draft with change or something that is in reviewer-submitted wth a rejection status of true
    } else if (!synced && userTier === 'reviewer') {
      if (candidate?.workflow.status === 'draft') {
        setShowSubmitForReviewButton(true);
      } else if (
        candidate?.workflow.status === 'reviewer-submitted' &&
        candidate?.workflow.rejectionStatus
      ) {
        // show submit for review button only after reviewer has saved an edit to the draft
        const savedStagingDraft = user._id === candidate.stagingDraft?.user;
        setShowSubmitForReviewButton(savedStagingDraft);
      } else {
        setShowSubmitForReviewButton(false);
      }
    } else {
      setShowSubmitForReviewButton(false);
    }
  };

  useEffect(() => {
    if (!candidate?.workflow) return;
    // run logic to deterine which buttons to display, based on the user permissions and where the workflow is at
    determineReviewButton();
    determineSubmitForReviewButton();
  }, [
    candidate?.workflow,
    publishPermission,
    editPermission,
    reviewerPermission,
  ]);

  const errorMessage = useMemo(() => {
    if (typeof error === 'string') {
      return error;
    }
    if (typeof error === 'object') {
      if (error?.type === 'FeathersError') {
        let messageToShow;
        if (error?.errors && Object.values(error.errors).length > 0) {
          messageToShow = Object.values(error.errors).join(' ');
        } else {
          messageToShow = error?.message;
        }
        switch (error.code) {
          case 400:
            if (messageToShow) return messageToShow;
            return 'There was a problem with the data you submitted. Please check the form and try again.';
          case 401:
          case 403:
            if (messageToShow) return messageToShow;
            return 'You are not authorized to perform this action.';
          default:
            return messageToShow;
        }
      } else return error?.message;
    }

    return 'There was an error when saving. Please try again!';
  }, [error]);

  // Declare functions
  const loadDataForCandidate = async (ln = 'en') => {
    setCandidate(null);
    setEditing(false);
    setRestoring(false);
    setReviewMode(false);
    setReviewNotesOnGoing(null);
    setSaving(false);
    setPublishing(false);
    try {
      const candidate = await feathers
        .getService('candidates')
        .get(id, {
          query: { ln },
        });
      setCandidate(candidate);
      const race = await feathers.getService('races').get(raceId);
      setRace(race);
      setElection(candidate?.election);

      // Check for in-progress candidate-profiler job created in last 10 minutes
      if (candidate?.jobs && Array.isArray(candidate.jobs)) {
        const now = moment();
        const inProgressJob = candidate.jobs.find((job: any) => {
          if (job.type === 'candidate-profiler' && job.status === 'INPROGRESS') {
            const createdAt = job.createdAt ? moment(job.createdAt) : null;
            if (createdAt && now.diff(createdAt, 'minutes') <= 10) {
              return true;
            }
          }
          return false;
        });
        if (inProgressJob?._id) {
          setActiveJobId(inProgressJob._id);
          setActiveJob(inProgressJob);
          setProfiling(true);
        }
      }
    } catch (err) {
      console.log(`Error in retrieving candidate: `, candidate);
      setError(err as APIError);
    }
  };

  // Polling function for job status
  useEffect(() => {
    if (!activeJobId || !feathers || !id) {
      return;
    }

    const pollJobStatus = async () => {
      try {
        const candidate = await feathers
          .getService('candidates')
          .get(id);
        
        const job = candidate?.jobs?.find((j: any) => `${j._id}` === `${activeJobId}`);
        
        if (!job) {
          // Job not found, stop polling
          setActiveJobId(null);
          setActiveJob(null);
          setProfiling(false);
          return;
        }

        // Check if job is more than 10 minutes old
        if (job.createdAt) {
          const jobAge = moment().diff(moment(job.createdAt), 'minutes');
          if (jobAge > 10) {
            setActiveJobId(null);
            setActiveJob(null);
            setProfiling(false);
            return;
          }
        }

        setActiveJob(job);

        console.log('job', job);

        // Handle job completion
        if (job.status === 'COMPLETED') {
          setActiveJobId(null);
          setActiveJob(null);
          setProfiling(false);
          loadDataForCandidate();
        } else if (job.status === 'CANCELLED') {
          setActiveJobId(null);
          setActiveJob(null);
          setProfiling(false);
          setShowCancelledModal(true);
        }
      } catch (err) {
        console.error('Error polling job status:', err);
      }
    };

    // Poll immediately, then every 5 seconds
    pollJobStatus();
    const interval = setInterval(pollJobStatus, 5000);

    return () => clearInterval(interval);
  }, [activeJobId, feathers, id]);

  useEffect(() => {
    if(feathers) {
      loadScrapedContent();
      const interval = setInterval(() => {
        loadScrapedContent();
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [feathers, id])

  const loadScrapedContent = async () => {
    try {
      const scrapedContentCount = await feathers.getService('scraped-content').find({
        query: {
          candidateId: id,
          $limit: 0,
        }
      });
      const numChunks = Math.ceil(scrapedContentCount.total / 10);
      const chunks = Array.from({ length: numChunks }, (_, i) => i);
      const scrapedContent = (await Promise.all(chunks.map(i => {
        return feathers.getService('scraped-content').find({
          query: {
            candidateId: id,
            $limit: 10,
            $skip: i * 10,
            $sort: { createdAt: -1 },
            $select: SCRAPED_CONTENT_METADATA_FIELDS,
          }
        })
      }))).map(res => res.data).flat();

      setScrapedContent(scrapedContent);
    } catch(err) {
      console.log(`Failed to load scraped content: `, err);
    }
  }

  const beginEdit = (e?: unknown, inReviewMode = false) => {
    // if not in review mode and not already showing the return to draft modal, check if the workflow status is above the users tier.
    // the modal should pop up if the user is editing something above their tier, and return before going to edit screen
    if (!showReturnToDraftModal && !inReviewMode) {
      if (userTier === 'researcher') {
        if (
          ['publisher-submitted', 'reviewer-submitted'].includes(
            candidate?.workflow.status as CandidateWorkflowStatus
          )
        ) {
          setShowReturnToDraftModal(true);
          return;
        } else if (
          candidate?.workflow.status === 'researcher-submitted' &&
          !candidate.workflow.rejectionStatus
        ) {
          setShowReturnToDraftModal(true);
          return;
        }
      }
      if (userTier === 'reviewer') {
        if (
          ['publisher-submitted'].includes(candidate?.workflow.status as CandidateWorkflowStatus)
        ) {
          setShowReturnToDraftModal(true);
          return;
        } else if (
          candidate?.workflow.status === 'reviewer-submitted' &&
          !candidate.workflow.rejectionStatus
        ) {
          setShowReturnToDraftModal(true);
          return;
        }
      }
    }

    setShowReturnToDraftModal(false);
    startEditTracker();
    // resetTimeTracker();

    setCandidateDraft(candidate?.stagingDraft ? {
      ...candidate.stagingDraft,
      removedAutoContent: candidate.removedAutoContent,
    } : undefined);

    setEditing(true);
  };

  const cancelEdits = () => {
    startEditTracker();
    setEditing(false);
    endEditTracker();
  };

  // Handler for receiving tracked times from EditCandidateProfile
  const handleTrackedTimesUpdate = (times: Record<string, number>) => {
    setTrackedTimesByArea(times);

    console.log('trackedTimesByArea', times);
  };

  const saveEdits = async () => {
    if (saving || !editing || !candidate?._id || !candidateDraft) {
      return;
    }
    setSaving(true);
    setError(null);
    const candidateUpdates = mapDraftToServerInputs(candidateDraft);

    // Include tracked times and scrapedContentChanges if available
    const patchData = {
      ...candidateUpdates,
      ...(candidateDraft?.issuesToCoverAdditional
        ? { issuesToCoverAdditional: candidateDraft?.issuesToCoverAdditional }
        : {}),
      ...(Object.keys(trackedTimesByArea).length > 0 ? { trackedTimesByArea } : {}),
      ...(candidateDraft.$scrapedContentChanges ? { $scrapedContentChanges: candidateDraft.$scrapedContentChanges } : {})
    };



    try {
      await feathers
        .getService('candidate-drafts')
        .patch(candidate._id, patchData);
      
      // Reset tracked times after successful save
      setTrackedTimesByArea({});
      
      loadDataForCandidate();
    } catch (err) {
      console.error(err);
      setError(err as APIError);
      const newDraft = newDraftAfterError(candidateDraft, err)
      setCandidateDraft(newDraft);
      setSaving(false);
    } finally {
      endEditTracker();
    }
  };

  const autoProfileWithReference = async (reference: {
    type: CandidateReferenceCategoryType.website | CandidateReferenceCategoryType.news;
    url: string;
    title?: string;
  }) => {
    if(saving || !editing || !candidate?._id) return;

    setSaving(true);
    setError(null);
    try {
      const updatedCandidate = await feathers
        .getService('candidate-drafts')
        .patch(candidate._id, { $appendReference: reference });

      setCandidate(updatedCandidate);
      setCandidateDraft({
        ...updatedCandidate.stagingDraft,
        removedAutoContent: updatedCandidate.removedAutoContent,
      });
      setTrackedTimesByArea({});
      endEditTracker();
      startEditTracker();
    } finally {
      setSaving(false);
    }
  };

  const finishRestore = async () => {
    if (saving || !restoring || !candidate?._id || !candidateDraft?._id) {
      return;
    }
    setSaving(true);
    setError(null);

    try {
      await feathers
        .getService('candidate-drafts')
        .patch(candidate._id, {
          $restoreToPreviousDraft: candidateDraft._id,
        });
      loadDataForCandidate();
    } catch (err) {
      console.error(err);
      setError(err as APIError);
      setSaving(false);
    }
  };


  // Handles a new candidate photo
  const deleteCandidate = async () => {
    if (deleteBlockedByRunningMate) {
      setDeleteModalOpen(true);
      return;
    }

    try {
      await feathers.getService('candidates').remove(id);
      history.push(`/elections/${election}/races/${raceId}`);
    } catch (err) {
      console.error(`Could not delete candidate`, err);
    }
  };

  const convertToRunningMate = async (targetRace: Race, primaryCandidateId: string) => {
    if(!candidate) return;

    await feathers.getService('candidates').patch(candidate._id, {
      race: targetRace._id,
      runningMate: {
        candidate: primaryCandidateId,
        isPrimary: false,
      },
    }, {
      query: {
        convertToRunningMate: true,
      },
    });
    setConvertToRunningMateModalOpen(false);
    history.push(
      `/elections/${targetRace.election}/races/${targetRace._id}/candidates/${candidate._id}`
    );
  };

  const publish = async () => {
    if (!publishable || publishing || !candidate?._id) {
      return;
    }
    setPublishing(true);

    try {
      await feathers
        .getService('candidate-drafts')
        .patch(candidate._id, {
          $publish: true,
        });
      loadDataForCandidate();
    } catch (err) {
      setPublishing(false);
      setError(err as APIError);
    }
  };

  // this is called when a researcher is ready for their draft to be reviewed by a reviewer
  const submitForReview = async () => {
    if (!candidate?._id) {
      return;
    }
    throw new Error('Not implemented');
    // try {
    //   setSaving(true);
    //   await feathers.getService('candidates').patch(candidate._id, {
    //     $workflowChange: 'promote',
    //   });
    //   loadDataForCandidate();
    // } catch (err) {
    //   setError(err as APIError);
    // } finally {
    //   setSaving(false);
    // }
  };

  // when submit from modal in review mode
  const submitReview = async () => {
    // submit or reject, include note and edits to candidate
    if (saving) {
      return;
    }
    setSaving(true);
    setError(null);
    // const candidateUpdates = null; //candidateFromFormInputs(formInputs);
    throw new Error('Not implemented');

    // try {
    //   await feathers.getService('candidates').patch(candidate._id, {
    //     $workflowChange: approved ? 'promote' : 'reject',
    //     note:
    //       notes && notes.trim().length > 0 ? notes.trim() : undefined,
    //     references: referencesInput,
    //     ...candidateUpdates,
    //   });
    //   loadDataForCandidate();
    //   setShowReviewModal(false);
    //   setEditing(false);
    // } catch (err) {
    //   setError(err);
    // } finally {
    //   endEditTracker();
    //   setSaving(false);
    // }
  };

  const candidateDraftWithIssues = useMemo(() => {
    const issuesTitlesByKey = candidate?.issues?.reduce(
      (acc, issue) => {
        acc[issue.key] = issue.title;
        return acc;
      },
      {} as Record<string, string>
    );
    return {
      ...candidateDraft,
      issues: candidateDraft?.issues?.map((issue: Partial<Issue>) => ({
        title: issue?.key ? issuesTitlesByKey?.[issue?.key] : null,
        ...issue,
      })),
    };
  }, [candidateDraft, candidate]);

  // Apply $scrapedContentChanges to scrapedContent to create scrapedContentMapped
  const scrapedContentMapped = useMemo(() => {
    if (!scrapedContent) {
      return scrapedContent;
    }

    if (!candidateDraft?.$scrapedContentChanges) {
      return scrapedContent;
    }

    const changesMap = new Map(
      candidateDraft.$scrapedContentChanges.map(change => [
        change.scrapedContentId,
        change.changes
      ])
    );

    // Filter out deleted items and apply type changes
    return scrapedContent
      .filter(sc => {
        const changes = changesMap.get(sc._id);
        // Filter out items marked for deletion
        return !changes || changes.delete !== true;
      })
      .map(sc => {
        const changes = changesMap.get(sc._id);
        if (!changes || changes.type === undefined) {
          return sc;
        }

        // Apply the changes to create a new scrapedContent object
        return {
          ...sc,
          type: changes.type
        };
      });
  }, [scrapedContent, candidateDraft?.$scrapedContentChanges]);

  const handleScrapedContentSaved = (updated: ScrapedContent) => {
    setScrapedContent(existing => (existing || []).map(item => (
      item._id === updated._id
        ? {
            ...item,
            status: updated.status,
            error: updated.error,
            addMethod: updated.addMethod,
            isMostRecent: updated.isMostRecent,
            updatedAt: updated.updatedAt,
          }
        : item
    )));
  };

  // On the initial component load...
  useEffect(() => {
    if (feathers && raceId) {
      // load data for the candidate
      loadDataForCandidate();
    }
  }, [feathers, raceId, id]);
  // useEffect(() => {
  //   if (feathers) loadDataForCandidate(activeLanguage);
  // }, [activeLanguage]);

  useEffect(() => {
    if (editing) {
      window.onbeforeunload = function () {
        return 'Are you sure you want to navigate away? Any unsaved changes will be lost.';
      };

      return () => {
        window.onbeforeunload = null;
      };
    } else {
      window.onbeforeunload = null;
    }
  }, [editing]);

  const candidatePreviousVersions = useMemo(() => {
    return [
      stagingDraft,
      ...(candidate?.previousDrafts || []),
    ].filter(Boolean);
  }, [candidate?.previousDrafts, stagingDraft]);

  if (!candidate || !race) {
    return <LoadingSpinner />;
  }

  const liveLink = window?.location?.href.includes('branch.vote') ? `https://www.branch.vote/races/${candidate?.raceKey}/candidates/${candidate?.official}` : `http://localhost:3030/races/${candidate?.raceKey}/candidates/${candidate?.official}`;
  const navTree = [
    {
      text:
        election && elections[election]?.name
          ? elections[election].name
          : '--',
      to: {
        pathname: `/elections/${election}/races`,
        search: racesSearch,
      },
    },
    {
      text: officeName || '--',
      to: {
        pathname: `/elections/${election}/races/${raceId}`,
        state: { racesSearch },
      },
    },
    { text: name || '' },
  ] as never[];
  const candidatePhotoPreview = (showTabs = true) => (
    <Top>
      <TopInner>
        <CirclePhoto
          size="large"
          photoPath={
            (restoring ? candidateDraft : candidate?.stagingDraft)
              ?.photoPathFace
          }
          hasVariants={
            (restoring ? candidateDraft : candidate?.stagingDraft)
              ?.hasVariants === true
          }
        />
        <TopText>
          <div
            style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
            }}
          >
            <Typography variant="h2" color="textPrimary">
              {candidate?.stagingDraft?.name}
            </Typography>
            {candidate?.party && race?.party !== 'N' && (
              <PartyTag
                party={candidate.party}
                style={{ marginLeft: '16px' }}
              />
            )}
          </div>
          <Typography
            variant="body1"
            color="textPrimary"
            style={{ marginTop: '8px', marginBottom: '8px' }}
          >
            {candidate?.name} is running for {race?.longName} in{' '}
            {race?.district?.longName}.
          </Typography>
          <LinkList
            links={
              (restoring ? candidateDraft : candidate?.stagingDraft)
                ?.links || []
            }
            align="flex-start"
          />
        </TopText>
      </TopInner>
      {showTabs && (
        <TabWrapper>
          <TabControl
            onChange={(i: number) => setActiveTab(i)}
            activeTab={activeTab}
            tabStyle={{ marginBottom: '-2px' }}
            align="center"
          >
            <Tab>ON THE ISSUES</Tab>
            <Tab>CAMPAIGN FINANCE</Tab>
            {electionOver && <Tab>ELECTION RESULTS</Tab>}
          </TabControl>
        </TabWrapper>
      )}
    </Top>
  );

  return (
    (<Wrapper>
      <Prompt
        when={Boolean(editing)}
        message="You have unsaved changes, are you sure you want to leave?"
      />
      <Dialog
        open={showReturnToDraftModal}
        onClose={() => setShowReturnToDraftModal(false)}
      >
        <DialogTitle id="alert-dialog-title">
          Are you sure you want to continue?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            This profile is currently awaiting review by the next
            person. If you edit the profile, it will remove it from
            review. You will need to submit for review again.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowReturnToDraftModal(false)}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              beginEdit();
              setShowReturnToDraftModal(false);
            }}
            color="primary"
          >
            Continue editing
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={error != null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        autoHideDuration={6000}
        style={{ position: 'absolute' }}
        onClose={() => setError(null)}
      >
        <Alert
          severity="error"
          onClose={() => setError(null)}
          elevation={6}
          variant="filled"
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <DataDetailToolbar
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          width: 'calc( 100% - 36px * 2 )',
          padding: '16px 36px 16px',
          height: '72px',
        }}
        onBack={{
          to: {
            pathname:
              onBack || `/elections/${election}/races/${raceId}`,
            state: { racesSearch },
          },
        }}
        navTree={navTree}
        actionButtonsComponent={
          <>
            {editing && (
              <ButtonContainer>
                <Button
                  variant={'text'}
                  color={'secondary'}
                  data-tip="Cancel Draft"
                  onClick={cancelEdits}
                  disabled={saving}
                >
                  {reviewMode ? 'Cancel' : 'Cancel'}
                </Button>
                {!reviewMode && (
                  <Button
                    variant={'contained'}
                    color={'secondary'}
                    style={{
                      color: '#FFFFFF',
                      marginLeft: '12px',
                    }}
                    disabled={saving}
                    data-tip="Save Draft"
                    onClick={saveEdits}
                  >
                    Save
                  </Button>
                )}
                {reviewMode && (
                  <Button
                    variant={'contained'}
                    color={'secondary'}
                    style={{
                      color: '#FFFFFF',
                      marginLeft: '12px',
                    }}
                    data-tip="Approve or reject the current version"
                    onClick={() => setShowReviewModal(true)}
                  >
                    Submit review
                  </Button>
                )}
              </ButtonContainer>
            )}
            {restoring && (
              <ButtonContainer>
                <Button
                  variant={'text'}
                  color={'secondary'}
                  data-tip="Cancel"
                  disabled={saving}
                  onClick={() => setRestoring(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant={'contained'}
                  color={'secondary'}
                  style={{
                    color: '#FFFFFF',
                    marginLeft: '12px',
                  }}
                  disabled={saving}
                  data-tip="Restore"
                  onClick={finishRestore}
                >
                  Restore
                </Button>
              </ButtonContainer>
            )}
            {!editing && !restoring && (
              <ButtonContainer>
                {
                  (profiling || (activeJobId && activeJob)) && (
                    <PulsingProgressContainer>
                      <Tooltip 
                        title={
                          activeJobId && activeJob ? (
                            <div>
                              <div style={{ marginBottom: '8px', fontWeight: 'bold' }}>Profiling in progress</div>
                              {activeJob.stepsComplete && activeJob.stepsComplete.length > 0 && (
                                <div>
                                  <div style={{ marginBottom: '4px' }}>Completed steps:</div>
                                  {activeJob.stepsComplete.map((step: string, idx: number) => (
                                    <div key={idx} style={{ marginLeft: '8px' }}>• {step}</div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div>Starting profiler...</div>
                          )
                        } 
                        placement="top"
                      >
                        <div style={{ position: 'relative', display: 'inline-flex' }}>
                          {activeJobId && activeJob && activeJob.totalSteps && activeJob.totalSteps.length > 0 ? (
                            <>
                              {/* Background grey circle */}
                              <CircularProgress
                                className="background-progress"
                                size={24}
                                variant="determinate"
                                value={100}
                                thickness={4.5}
                                sx={{
                                  color: '#e0e0e0',
                                  position: 'absolute',
                                }}
                              />
                              {/* Actual progress circle */}
                              <CircularProgress 
                                size={24} 
                                variant="determinate"
                                value={Math.max(
                                  10,
                                  (activeJob.stepsComplete?.length || 0) / activeJob.totalSteps.length * 100
                                )}
                                thickness={4.5}
                                sx={{
                                  position: 'relative',
                                }}
                              />
                            </>
                          ) : (
                            <CircularProgress 
                              size={24} 
                              variant="indeterminate"
                              thickness={4.5}
                            />
                          )}
                        </div>
                      </Tooltip>
                    </PulsingProgressContainer>
                  )
                }
                <IconButton
                  aria-label="more"
                  aria-controls="long-menu"
                  aria-haspopup="true"
                  onClick={(e) =>
                    setMoreButtonClicked(e.currentTarget)
                  }
                  size="large">
                  <MoreVertIcon />
                </IconButton>
                {editPermission && editingEnabled && (
                  <IconButton title="Edit Draft" onClick={beginEdit} size="large">
                    <EditIcon />
                  </IconButton>
                )}
                {publishable && (
                  <Button
                    onClick={publish}
                    variant={'contained'}
                    color={'secondary'}
                    disabled={publishing}
                    style={{
                      paddingTop: '10px',
                      marginLeft: '6px',
                    }}
                  >
                    {publishing ? 'Publishing...' : 'Publish'}
                  </Button>
                )}
                {showSubmitForReviewButton && (
                  <Button
                    onClick={submitForReview}
                    variant={'contained'}
                    disabled={saving}
                    color={'secondary'}
                    style={{
                      paddingTop: '10px',
                      marginLeft: '6px',
                    }}
                  >
                    Submit for Review
                  </Button>
                )}
                {showReviewButton && (
                  <Button
                    onClick={(e) => {
                      setReviewMode(true);
                      beginEdit(e, true);
                    }}
                    variant={'contained'}
                    color={'secondary'}
                    style={{
                      paddingTop: '10px',
                      marginLeft: '6px',
                    }}
                  >
                    {candidate?.stagingDraft?.user === user._id
                      ? 'Continue Review'
                      : 'Begin Review'}
                  </Button>
                )}
              </ButtonContainer>
            )}
          </>
        }
      />
      <WrapperInner>
        {!editing && (
          <Main>
            <CandidateInfoWrapper editing={0}>
              {candidatePhotoPreview(!useLinkCandidateEditor)}
              {!useLinkCandidateEditor && (
                <>
                  {activeTab === 0 &&
                    (someContent ? (
                      <StancesDisplay
                        candidate={
                          restoring
                            ? (candidateDraftWithIssues as CandidateDraft)
                            : candidate?.stagingDraft
                        }
                      />
                    ) : (
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: '36px',
                        }}
                      >
                        <Typography
                          variant="body1"
                          style={{
                            fontWeight: 'bold',
                            letterSpacing: '2px',
                            opacity: 0.5,
                          }}
                        >
                          NO INFORMATION FOR THIS CANDIDATE YET
                        </Typography>
                      </div>
                    ))}
                  {activeTab === 1 && (
                    <>
                      {candidate?.finance && (
                        <CampaignFinance finance={candidate?.finance} />
                      )}
                    </>
                  )}
                </>
              )}
            </CandidateInfoWrapper>
          </Main>
        )}
        {editing && (
          <Main>
            <CandidateInfoWrapper editing={1}>
              {useLinkCandidateEditor ? (
                <LinkCandidateProfileEditor
                  value={candidateDraft}
                  onChange={setCandidateDraft}
                  race={race}
                  candidateId={candidate?._id}
                  electionKey={election}
                  photoOptions={candidate?.photoOptions}
                  disabled={saving}
                  showFullPageLink={false}
                />
              ) : (
                <EditCandidateProfile
                  coveragePlan={coveragePlan}
                  value={candidateDraft}
                  onChange={setCandidateDraft}
                  onChangeIssue={(newIssue: Issue, issueKey: string) => {
                    setCandidateDraft(existingDraft => {
                      if (!existingDraft) return existingDraft;
                      const existingDraftIssues = existingDraft.issues || [];
                      const newIssues = existingDraftIssues.map(issue => {
                        if(issue.key === issueKey) {
                          return newIssue
                        }
                        return issue
                      })

                      return {
                        ...existingDraft,
                        issues: newIssues
                      }
                    })
                  }}
                  election={election}
                  onTrackedTimesUpdate={handleTrackedTimesUpdate}
                  race={race}
                  photoOptions={candidate?.photoOptions}
                  candidateId={candidate?._id}
                  disabled={saving}
                />
              )}
            </CandidateInfoWrapper>
          </Main>
        )}
        <SidePane
            sectionTitles={
              editing
                ? [
                    candidate?.priorityLevel ? 'Priority' : null,
                    reviewMode ? 'Your review notes' : null,
                    'Information sources',
                    candidate?.workflow?.notes?.length > 0
                      ? 'Reviewer notes'
                      : undefined,
                  ].filter(Boolean)
                : null
            }
            sectionsInitiallyExpanded={
              editing
                ? [
                    candidate?.priorityLevel ? true : null,
                    reviewMode ? true : null,
                    true,
                    candidate?.workflow?.notes?.length > 0
                      ? true
                      : null,
                  ].filter(v => v !== null)
                : null
            }
          >
            {editing && candidate?.priorityLevel && (
              <TimeTracker
                coveragePlan={coveragePlan}
                priorityLevel={candidate?.priorityLevel}
              />
            )}
            {editing && candidateDraft && (
              <>
                {reviewMode && (
                  <ResearchCandidate
                    onChangeNotes={setReviewNotesOnGoing}
                    onFinishReview={() => setShowReviewModal(true)}
                  />
                )}
                {!useLinkCandidateEditor && (
                  <ReferenceEditor
                    priorityLevel={candidate?.priorityLevel}
                    onChangeActiveDraft={(update, scrapedContentChanges) => {
                      setCandidateDraft({
                        ...candidateDraft, 
                        ...update,
                        ...(scrapedContentChanges ? { $scrapedContentChanges: scrapedContentChanges } : {})
                      });
                    }}
                    disabled={saving}
                    activeDraft={candidateDraft}
                    scrapedContent={scrapedContentMapped}
                    onScrapedContentSaved={handleScrapedContentSaved}
                    onAutoProfileReference={autoProfileWithReference}
                  />
                )}
                {candidate?.workflow?.notes?.length > 0 && (
                  <ReviewerContentWrapper>
                    {(candidate?.workflow?.notes || [])?.map(
                      (note) => (
                        <Note key={note._id}>
                          <StyledMarkdown>{note.text}</StyledMarkdown>
                          <p className="date">
                            {note.createdAt
                              ? moment.utc(note.createdAt).fromNow()
                              : ''}
                          </p>
                        </Note>
                      )
                    )}
                  </ReviewerContentWrapper>
                )}
              </>
            )}
            {restoring && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                  alignItems: 'stretch',
                  maxWidth: '400px',
                  marginTop: '12px',
                }}
              >
                <Typography
                  variant={'h3'}
                  style={{ marginBottom: '16px' }}
                >
                  Restore previous version
                </Typography>
                {candidatePreviousVersions.map(draft => (
                  <DraftPreview
                    draft={draft}
                    showProgressBar
                    onClick={() => setCandidateDraft(draft as CandidateDraft)}
                    isSelected={candidateDraft?._id === draft._id}
                  />
                ))}
              </div>
            )}
            {!editing && !restoring && (
              <CandidateDataFieldEditor
                candidateId={id}
                race={race}
                scrapedContent={scrapedContent}
                onError={(err: unknown) => setError(err as APIError)}
                onAddRunningMate={() => setShowCreateRunningMate(true)}
                refreshToken={runningMateRefreshToken}
              />
            )}
          </SidePane>
      </WrapperInner>
      <Dialog
        open={showCreateRunningMate}
        onClose={() => setShowCreateRunningMate(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogContent style={{ padding: '48px' }}>
          <CreateCandidate
            raceData={race}
            runningMateForCandidateId={candidate?._id}
            onClose={() => setShowCreateRunningMate(false)}
            onProcessingStart={() => undefined}
            onSuccess={() => {
              loadDataForCandidate();
              setRunningMateRefreshToken((token) => token + 1);
            }}
            onError={(err: APIError) => {
              setError(err);
              setShowCreateRunningMate(false);
            }}
          />
        </DialogContent>
      </Dialog>
      <Dialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
      >
        <DialogTitle id="alert-dialog-title">
          {deleteBlockedByRunningMate
            ? 'Cannot delete this candidate'
            : 'Are you sure you want to delete the candidate?'}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {deleteBlockedByRunningMate
              ? 'You must delete the running mate first.'
              : `If you delete the candidate after the election has been
                published, it could have negative consequences for voters.`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteModalOpen(false)}
            color="primary"
          >
            {deleteBlockedByRunningMate ? 'OK' : 'Cancel'}
          </Button>
          {!deleteBlockedByRunningMate && (
            <Button
              onClick={() => {
                setDeleteModalOpen(false);
                deleteCandidate();
              }}
              color="primary"
            >
              Delete
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {Boolean(showReviewModal) && (
        <Modal onClose={() => setShowReviewModal(false)}>
          <Typography variant="h3" style={{ marginBottom: '24px' }}>
            Submit review
          </Typography>
          <ResearchCandidate
            notesInitial={reviewNotesOnGoing}
            onSubmit={submitReview}
          />
        </Modal>
      )}
      <Menu
        id="long-menu"
        anchorEl={moreButtonClicked}
        keepMounted
        open={Boolean(moreButtonClicked)}
        onClose={() => setMoreButtonClicked(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={() => {
            setMoreButtonClicked(null);
          }}
        >
          <a
            href={liveLink}
            target="_blank"
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              textDecoration: 'none',
            }}
            rel="noreferrer"
          >
            <ListItemIcon>
              <PreviewIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>View live</ListItemText>
          </a>
        </MenuItem>
        {
          translationPermission &&
          <>
            <MenuItem
              onClick={() => {
                setMoreButtonClicked(null);
              }}
            >
              <div style={{ width: '100%' }}>
                <TranslationPairDownload 
                  serviceName='candidates' 
                  objectId={candidate._id}
                  buttonComponent={({ onClick }: { onClick: () => void }) => (
                    <div 
                      style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onClick();
                      }}
                    >
                      <ListItemIcon>
                        <DownloadTranslateIcon>
                          <TranslateIcon fontSize="small" />
                          <DownloadIcon className="download-icon" fontSize="small" />
                        </DownloadTranslateIcon>
                      </ListItemIcon>
                      <ListItemText>Download translations</ListItemText>
                    </div>
                  )}
                />
              </div>
            </MenuItem>
            <MenuItem
              onClick={() => {
                setMoreButtonClicked(null);
              }}
            >
              <div style={{ width: '100%' }}>
                <TranslationPairEditor 
                  serviceName='candidates' 
                  objectId={candidate._id}
                  buttonComponent={({ onClick }: { onClick: () => void }) => (
                    <div 
                      style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        onClick();
                      }}
                    >
                      <ListItemIcon>
                        <EditTranslateIcon>
                          <TranslateIcon fontSize="small" />
                          <EditIcon className="edit-icon" fontSize="small" />
                        </EditTranslateIcon>
                      </ListItemIcon>
                      <ListItemText>Edit translations</ListItemText>
                    </div>
                  )}
                />
              </div>
            </MenuItem>
          </>
        }
        {
          translationPermission &&
          <MenuItem
            onClick={() => {
              setMoreButtonClicked(null);
            }}
          >
            <RetranslateTrigger 
              serviceName="candidates" 
              objectId={candidate._id}
              trigger={
                <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                  <ListItemIcon>
                    <RetranslateIcon>
                      <TranslateIcon fontSize="small" />
                      <RefreshIcon className="refresh-icon" fontSize="small" />
                    </RetranslateIcon>
                  </ListItemIcon>
                  <ListItemText>Retranslate</ListItemText>
                </div>
              }
            />
          </MenuItem>
        }
        <MenuItem
          disabled={profiling || !candidate}
          onClick={() => {
            setMoreButtonClicked(null);
            runProfiler()
          }}
        >
          <ListItemIcon >
            <AutoAwesome fontSize="small" />
          </ListItemIcon>
          <ListItemText>Auto-profile</ListItemText>
        </MenuItem>
        {profiling && activeJobId && (
          <MenuItem
            onClick={() => {
              cancelProfiler();
            }}
          >
            <ListItemIcon>
              <CancelIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Cancel Profiler</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setMoreButtonClicked(null);
            setShowGiveFeedbackModal(true);
          }}
        >
          <ListItemIcon>
            <EditNoteIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Give feedback</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            revertToPreviousVersion();
            setMoreButtonClicked(null);
          }}
        >
          <ListItemIcon>
            <HistoryOutlined fontSize="small" />
          </ListItemIcon>
          <ListItemText>Restore previous version</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setConvertToRunningMateModalOpen(true);
            setMoreButtonClicked(null);
          }}
        >
          <ListItemIcon>
            <SwapHorizIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Convert to running mate</ListItemText>
        </MenuItem>
        {deletePermission && (
          <MenuItem
            onClick={() => {
              setDeleteModalOpen(true);
              setMoreButtonClicked(null);
            }}
          >
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete candidate</ListItemText>
          </MenuItem>
        )}
        {Boolean(showGiveFeedbackModal) && (
          <Modal onClose={() => setShowGiveFeedbackModal(false)}>
            <CreateProfileFeedback
              candidateId={candidate._id}
              drafts={candidatePreviousVersions}
              onClose={() => setShowGiveFeedbackModal(false)}
              onCreate={(feedback) => {
                history.push(
                  `/elections/${election}/races/${raceId}/candidates/${id}/feedback/${feedback._id}?confirm=true`
                );
              }}
            />
          </Modal>
        )}
        <Dialog
          open={showProfilerConfirmModal}
          onClose={() => setShowProfilerConfirmModal(false)}
        >
          <DialogTitle>Confirm Auto-Profile</DialogTitle>
          <DialogContent>
            <DialogContentText>
              This candidate has already been profiled and reviewed, profiling them will overwrite their data. Are you sure you want to do this?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowProfilerConfirmModal(false)}>
              Cancel
            </Button>
            <Button onClick={() => runProfiler(true)} color="primary" variant="contained">
              Confirm
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={showCancelledModal}
          onClose={() => setShowCancelledModal(false)}
        >
          <DialogTitle>Profiling Cancelled</DialogTitle>
          <DialogContent>
            <DialogContentText>
              The profiling job was cancelled. This candidate was not profiled because the automatic profiling conditions were not met.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowCancelledModal(false)} color="primary" variant="contained">
              OK
            </Button>
          </DialogActions>
        </Dialog>
        {/* <LanguageSelectButton
          selected={activeLanguage}
          onSelect={setActiveLanguage}
        /> */}
      </Menu>
      <ConvertToRunningMateModal
        open={convertToRunningMateModalOpen}
        onClose={() => setConvertToRunningMateModalOpen(false)}
        onConvert={convertToRunningMate}
        currentRace={race}
      />
    </Wrapper>)
  );
};

const Wrapper = styled.div`
  width: calc(100%);
  height: calc(100vh);
  overflow-y: scroll;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const WrapperInner = styled.div`
  padding: 0px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  height: calc(100% - 72px);
  max-height: calc(100% - 72px);
  width: 100%;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: -10px;

  div {
    margin: 0 0 0 0;
  }
`;

const CandidateInfoWrapper = styled.div<{ editing: 0 | 1 }>`
  ${props => props.editing ? `` : `width: calc(700px);
  max-width: 700px;`}
  padding: 32px 0 32px 64px;
  align-self: start;

  display: flex;
  flex-direction: column;
`;

const ReviewerContentWrapper = styled.div``;

const Note = styled.div`
  display: flex;
  flex-direction: column;
  padding: 5px 10px;
  font-size: 12px;
  .date {
    font-size: 10px;
    color: ${(props) => props.theme.colors.subtleGrey};
    align-self: flex-end;
  }
`;

const Top = styled.div`
  padding: 24px;
  width: calc(100% - 24px * 2);

  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const TopInner = styled.div`
  width: 100%;
  max-width: 700px;
  align-self: center;

  position: relative;
  display: flex;
  flex-direction: row;
  align-items: flex-start;

  @media (min-width: 500px) {
    align-items: center;
  }
`;

const TopText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  flex: 1;
  margin-left: 12px;
`;

const Main = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  margin-left: -24px;
  padding: 0 0 0;
  border-top: 1px solid #eeeeee;
  width: calc(100% + 24px * 2);
  background-color: #ffffff;
  overflow-y: scroll;
`;

const TabWrapper = styled.div`
  width: 100%;
  margin-top: 8px;
  margin-bottom: calc(-24px + 2px);
`;

export default CandidateDetail;
